import { useEffect, useMemo, useState } from "react";
import { api, apiUrl, fmtINR, fmtCompact, fmtPeriod } from "@/lib/api";
import { PageHeader, Section } from "@/components/Shared";
import {
  Banknote, Users, TrendingUp, TrendingDown, FileText, Loader2, Building2, Receipt, Download,
  Search, ArrowUp, ArrowDown, X, MessageCircle, ExternalLink, BookOpen, LifeBuoy,
  Layers, Percent,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  CartesianGrid, AreaChart, Area,
} from "recharts";
import { toast } from "sonner";

// Big rupee values (lakhs / crores) wrap onto two lines at text-3xl. Use the
// compact variant ("₹ 5.32 Cr") above 1 lakh to keep all KPIs single-line and
// equal-height. Smaller numbers stay precise.
const fmtKpi = (n) => {
  const x = Math.abs(Number(n || 0));
  if (x >= 1e5) return `₹ ${fmtCompact(n)}`;
  return `₹ ${fmtINR(n, 0)}`;
};

// ============== PARTNER DASHBOARD ==========================================

// ============== PARTNER DASHBOARD ==========================================

// MoM delta badge — identical look + math to the admin Merchants table.
// Hidden when delta is null (no prior baseline), near-zero, or merchant
// is deactivated.
function PartnerMomBadge({ deltaPct, status }) {
  if (status === "Deactivated") return null;
  if (deltaPct == null) return null;
  if (Math.abs(deltaPct) < 0.05) return null;
  const up = deltaPct > 0;
  const Icon = up ? TrendingUp : TrendingDown;
  const cls = up
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-red-50 text-red-700 border-red-200";
  return (
    <span
      className={`ml-1.5 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border ${cls}`}
      data-testid="partner-mom-badge"
    >
      <Icon size={10} strokeWidth={2.5} />
      {up ? "+" : ""}{deltaPct.toFixed(1)}%
    </span>
  );
}

// Sortable "My Merchants" table on the partner dashboard. Pulls from
// /portal/partner/merchants which already filters by signed-in partner.
function PartnerMerchantList({ data }) {
  const [sortBy, setSortBy] = useState({ key: "last_month_payout", dir: "desc" });
  const [q, setQ] = useState("");

  if (data == null) {
    return (
      <Section title="My merchants" testid="partner-merchants">
        <div className="p-5 text-[12.5px] text-[var(--muted)]"><Loader2 size={14} className="inline animate-spin mr-1.5" />Loading merchants…</div>
      </Section>
    );
  }
  const rows = data?.rows || [];
  if (rows.length === 0) {
    return (
      <Section title="My merchants" testid="partner-merchants">
        <div className="p-5 text-[12.5px] text-[var(--muted)]">No merchants assigned yet. Contact admin to get onboarded.</div>
      </Section>
    );
  }

  const filtered = q
    ? rows.filter((r) => {
        const needle = q.toLowerCase();
        return [r.merchant_id, r.merchant_name, r.gateway, r.lob]
          .some((s) => (s || "").toLowerCase().includes(needle));
      })
    : rows;

  const sorted = [...filtered].sort((a, b) => {
    const va = a[sortBy.key]; const vb = b[sortBy.key];
    if (typeof va === "number" && typeof vb === "number") {
      return sortBy.dir === "asc" ? va - vb : vb - va;
    }
    return sortBy.dir === "asc"
      ? String(va || "").localeCompare(String(vb || ""))
      : String(vb || "").localeCompare(String(va || ""));
  });

  const toggle = (k) => setSortBy((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));
  const SortTh = ({ k, children, align }) => (
    <th className={align === "right" ? "text-right" : ""}>
      <button type="button" onClick={() => toggle(k)} data-testid={`partner-sort-${k}`} className="inline-flex items-center gap-1 hover:text-[var(--ink)]">
        {children}
        {sortBy.key === k && (sortBy.dir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </button>
    </th>
  );

  const statusBadge = (st, deactivationDate) => {
    if (st === "Deactivated") {
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border bg-amber-50 text-amber-800 border-amber-200" title={deactivationDate ? `Deactivated on ${String(deactivationDate).slice(0, 10)}` : ""}>Deactivated</span>;
    }
    if (st === "Inactive") {
      return <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border bg-[var(--surface-2)] text-[var(--muted)] border-[var(--line-2)]">Inactive</span>;
    }
    return <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-sm border bg-emerald-50 text-emerald-700 border-emerald-200">Active</span>;
  };

  return (
    <Section title={`My merchants · ${rows.length}`} testid="partner-merchants">
      {rows.length > 6 && (
        <div className="px-5 pt-4 pb-2">
          <div className="relative max-w-sm">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
            <input
              data-testid="partner-merchants-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search merchant id, name, gateway…"
              className="w-full h-8 pl-7 pr-2 text-[12.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
            />
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="table-data w-full">
          <thead>
            <tr>
              <SortTh k="merchant_id">Merchant ID</SortTh>
              <SortTh k="merchant_name">Name</SortTh>
              <SortTh k="gateway">Gateway / LOB</SortTh>
              <SortTh k="status">Status</SortTh>
              <SortTh k="last_month_volume" align="right">Volume ({fmtPeriod(data.current_period)})</SortTh>
              <SortTh k="last_month_payout" align="right">Payout ({fmtPeriod(data.current_period)})</SortTh>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={r.program_id} data-testid={`partner-merchant-row-${r.merchant_id}`}>
                <td className="num">{r.merchant_id}</td>
                <td className="font-medium">
                  {r.merchant_name}
                  <PartnerMomBadge deltaPct={r.mom_delta_pct} status={r.status} />
                </td>
                <td className="text-[12px] text-[var(--ink-2)]">{r.gateway} · {r.lob}</td>
                <td>{statusBadge(r.status, r.deactivation_date)}</td>
                <td className="text-right num">{r.last_month_volume > 0 ? `₹ ${fmtINR(r.last_month_volume, 0)}` : <span className="text-[var(--muted)]">—</span>}</td>
                <td className="text-right num font-medium">{r.last_month_payout > 0 ? `₹ ${fmtINR(r.last_month_payout, 2)}` : <span className="text-[var(--muted)]">—</span>}</td>
              </tr>
            ))}
            {sorted.length === 0 && (
              <tr><td colSpan={6} className="text-center text-[var(--muted)] py-6 text-[12.5px]">No merchants match "{q}".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

export function PartnerDashboard() {
  const [data, setData] = useState(null);
  const [merchantsData, setMerchantsData] = useState(null);
  useEffect(() => {
    api.get("/portal/partner/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load dashboard"));
    // Fire the merchants list call in parallel — both endpoints are
    // independent. Best-effort: if it 500s the dashboard still renders
    // the KPIs + trend chart minus the merchant block.
    api.get("/portal/partner/merchants")
      .then((r) => setMerchantsData(r.data))
      .catch(() => setMerchantsData({ rows: [] }));
  }, []);

  if (!data) return <PortalLoading />;

  const { partner, kpis, trend } = data;

  return (
    <div data-testid="partner-dashboard">
      <PageHeader
        title={`Welcome, ${partner.partner_name}`}
        subtitle={`Partner ID ${partner.partner_id} · ${partner.compensation_model || "—"}`}
      />
      <div className="p-4 sm:p-10 max-w-6xl space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 auto-rows-fr">
          <KpiCard
            icon={Banknote}
            label="Latest payout"
            value={fmtKpi(kpis.current_period_payout)}
            sub={fmtPeriod(kpis.current_period)}
            testid="partner-kpi-payout"
          />
          <KpiCard
            icon={TrendingUp}
            label="Latest volume"
            value={fmtKpi(kpis.current_period_volume)}
            sub={fmtPeriod(kpis.current_period)}
            testid="partner-kpi-volume"
          />
          <KpiCard
            icon={Users}
            label="Merchants assigned"
            value={kpis.merchants_count}
            sub="Across all PGs"
            testid="partner-kpi-merchants"
          />
          <KpiCard
            icon={Receipt}
            label="TDS rate"
            value={`${(Number(partner.tds_rate || 0) * 100).toFixed(2)}%`}
            sub={partner.gst_applicable ? "GST 18% applicable" : "GST not applicable"}
            testid="partner-kpi-tax"
          />
        </div>

        <PartnerMerchantList data={merchantsData} />

        <Section title="Last 6 months — payout trend" testid="partner-trend">
          <div className="p-5">
            {trend.length === 0 ? (
              <div className="text-[12.5px] text-[var(--muted)]">No payout history yet.</div>
            ) : (
              <div style={{ minHeight: 240, width: "100%" }}>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={trend} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="payoutFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.32} />
                        <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" />
                    <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: "var(--muted)" }} />
                    <YAxis tickFormatter={(v) => `₹${fmtINR(v)}`} tick={{ fontSize: 11, fill: "var(--muted)" }} width={70} />
                    <Tooltip
                      labelFormatter={fmtPeriod}
                      formatter={(v) => `₹ ${fmtINR(v)}`}
                      contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4 }}
                    />
                    <Area type="monotone" dataKey="payout" stroke="var(--accent)" strokeWidth={2} fill="url(#payoutFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============== PARTNER PAYOUTS ============================================

export function PartnerPayouts() {
  const [period, setPeriod] = useState("");
  const [data, setData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);

  const refetch = (p) => {
    setLoading(true);
    api.get(`/portal/partner/payouts${p ? `?period=${p}` : ""}`)
      .then((r) => {
        setData(r.data);
        if (!period && r.data?.period) setPeriod(r.data.period);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load payouts"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { refetch(""); }, []);

  // Build periods list from the latest 6 months pulled from the dashboard
  useEffect(() => {
    api.get("/portal/partner/dashboard").then((r) => {
      setPeriods((r.data?.trend || []).map((t) => t.period).reverse());
    }).catch(() => {});
  }, []);

  const totals = data?.totals || {};
  const rows = data?.rows || [];
  const [q, setQ] = useState("");
  const [gw, setGw] = useState("");
  const [lob, setLob] = useState("");
  const [sort, setSort] = useState({ field: "partner_payout", dir: "desc", numeric: true });
  const gateways = useMemo(() => Array.from(new Set(rows.map((r) => r.gateway).filter(Boolean))).sort(), [rows]);
  const lobs = useMemo(() => Array.from(new Set(rows.map((r) => r.lob).filter(Boolean))).sort(), [rows]);
  const visible = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let out = rows;
    if (gw) out = out.filter((r) => r.gateway === gw);
    if (lob) out = out.filter((r) => r.lob === lob);
    if (ql) {
      out = out.filter((r) =>
        String(r.merchant_name || "").toLowerCase().includes(ql)
        || String(r.merchant_id || "").toLowerCase().includes(ql)
        || String(r.gateway || "").toLowerCase().includes(ql)
        || String(r.lob || "").toLowerCase().includes(ql)
      );
    }
    return applySort(out, sort);
  }, [rows, q, gw, lob, sort]);

  return (
    <div data-testid="partner-payouts">
      <PageHeader
        title="Payouts"
        subtitle={`Merchant-level breakdown for ${fmtPeriod(data?.period) || "—"}`}
      />
      <div className="p-4 sm:p-10 max-w-6xl space-y-5">
        <div className="flex items-center gap-2 text-[12px]">
          <span className="text-[var(--muted)] uppercase tracking-wider font-semibold">Period</span>
          <select
            data-testid="period-picker"
            value={period}
            onChange={(e) => { setPeriod(e.target.value); refetch(e.target.value); }}
            className="h-8 px-2 border border-[var(--line-2)] bg-[var(--surface)] rounded-sm text-[12.5px]"
          >
            {periods.length === 0 && data?.period && <option value={data.period}>{fmtPeriod(data.period)}</option>}
            {periods.map((p) => <option key={p} value={p}>{fmtPeriod(p)}</option>)}
          </select>
        </div>

        <Section title="Totals" testid="partner-payouts-totals">
          <div className="p-5 grid grid-cols-2 sm:grid-cols-5 gap-3 text-[13px]">
            <Stat label="Merchants" value={totals.rows ?? 0} />
            <Stat label="Payout" value={`₹ ${fmtINR(totals.payout)}`} />
            <Stat label={`+ GST ${(Number(totals.gst_rate || 0) * 100).toFixed(0)}%`} value={`₹ ${fmtINR(totals.gst)}`} />
            <Stat label={`− TDS ${(Number(totals.tds_rate || 0) * 100).toFixed(2)}%`} value={`₹ ${fmtINR(totals.tds)}`} />
            <Stat label="Net Payable" value={`₹ ${fmtINR(totals.net_payable)}`} highlight />
          </div>
        </Section>

        <Section title={`${visible.length} of ${rows.length} merchants`} testid="partner-payouts-table">
          <FilterBar
            q={q} setQ={setQ}
            placeholder="Search merchant / MID / PG / LOB…"
            filters={[
              { key: "gw", label: "Gateway", value: gw, options: gateways, onChange: setGw },
              { key: "lob", label: "LOB", value: lob, options: lobs, onChange: setLob },
            ]}
            onClear={() => { setQ(""); setGw(""); setLob(""); }}
            testidPrefix="pp"
          />
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead>
                <tr>
                  <SortHeader field="merchant_name" sort={sort} setSort={setSort}>Merchant</SortHeader>
                  <SortHeader field="merchant_id" sort={sort} setSort={setSort}>MID</SortHeader>
                  <SortHeader field="gateway" sort={sort} setSort={setSort}>PG</SortHeader>
                  <SortHeader field="lob" sort={sort} setSort={setSort}>LOB</SortHeader>
                  <SortHeader field="processed_volume" sort={sort} setSort={setSort} align="right" numeric>Volume</SortHeader>
                  <SortHeader field="partner_payout" sort={sort} setSort={setSort} align="right" numeric>Payout</SortHeader>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--muted)]"><Loader2 size={14} className="animate-spin inline mr-2" />Loading…</td></tr>
                )}
                {!loading && visible.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--muted)]">No payouts match the current filters.</td></tr>
                )}
                {visible.map((r) => (
                  <tr key={r.merchant_id}>
                    <td>{r.merchant_name}</td>
                    <td className="num">{r.merchant_id}</td>
                    <td>{r.gateway}</td>
                    <td>{r.lob}</td>
                    <td className="text-right num">₹ {fmtINR(r.processed_volume)}</td>
                    <td className="text-right num font-medium">₹ {fmtINR(r.partner_payout)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============== PARTNER STATEMENTS =========================================

export function PartnerStatements() {
  const [data, setData] = useState(null);
  const [periods, setPeriods] = useState([]);
  const [closedSet, setClosedSet] = useState(null);  // Set<period> of closed periods for this partner
  useEffect(() => {
    Promise.all([
      api.get("/portal/partner/dashboard"),
      // Closed periods that have a snapshot row for THIS partner. Until we have
      // that, we render no buttons (instead of buttons that 404). Partner
      // dashboard already returns the list of period closes server-side via
      // closed_periods (id-only summary).
      api.get("/portal/partner/closed-periods").catch(() => ({ data: [] })),
    ]).then(([dash, cp]) => {
      setData(dash.data);
      setPeriods((dash.data?.trend || []).slice().reverse());
      setClosedSet(new Set((cp.data || []).map((c) => c.period)));
    }).catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  }, []);
  if (!data) return <PortalLoading />;

  const download = (period) => {
    // Native-browser download via Content-Disposition: attachment. The httponly
    // access_token cookie travels along, and we sidestep the axios refresh
    // interceptor entirely (which can throw a generic "Script error" in dev
    // when a 401-then-refresh-then-retry chain happens with responseType:'blob').
    // If the period isn't closed yet, the server returns 404 and the browser
    // shows its own "this site can't be reached / not found" tab — much more
    // graceful than a runtime-error overlay. We surface a toast first to set
    // expectations.
    const partner_id = data.partner.partner_id;
    toast.message(`Preparing ${fmtPeriod(period)} statement…`);
    window.open(apiUrl(`/period-closes/${period}/pdf/${partner_id}`), "_blank");
  };

  return (
    <div data-testid="partner-statements">
      <PageHeader title="Statements" subtitle="Download finalised monthly payout statements" />
      <div className="p-4 sm:p-10 max-w-3xl">
        <Section title="Available periods">
          <div className="divide-y divide-[var(--line)]">
            {periods.length === 0 && (
              <div className="p-5 text-[12.5px] text-[var(--muted)]">No statements yet.</div>
            )}
            {periods.map((t) => {
              const isClosed = closedSet ? closedSet.has(t.period) : false;
              return (
              <div key={t.period} className="p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[14px] font-medium">{fmtPeriod(t.period)}</div>
                  <div className="text-[12px] text-[var(--muted)]">
                    Volume ₹ {fmtINR(t.volume)} · Payout ₹ {fmtINR(t.payout)} · {t.merchants} merchants
                  </div>
                </div>
                {isClosed ? (
                  <button
                    onClick={() => download(t.period)}
                    data-testid={`download-statement-${t.period}`}
                    className="inline-flex items-center gap-1.5 h-9 px-3 border border-[var(--line-2)] hover:border-[var(--accent)] hover:text-[var(--accent)] rounded-sm text-[12.5px] font-medium transition-colors"
                  >
                    <Download size={13} /> Download PDF
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 h-9 px-3 text-[11.5px] text-[var(--muted)] border border-dashed border-[var(--line-2)] rounded-sm">
                    Awaiting period close
                  </span>
                )}
              </div>
              );
            })}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============== MERCHANT DASHBOARD =========================================

export function MerchantDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/portal/merchant/dashboard")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load dashboard"));
  }, []);
  if (!data) return <PortalLoading />;

  const { merchant, kpis, trend, gateway_breakdown } = data;
  return (
    <div data-testid="merchant-dashboard">
      <PageHeader
        title={`Welcome, ${merchant.merchant_name}`}
        subtitle={`${merchant.mids.length} active MID${merchant.mids.length === 1 ? "" : "s"} across ${merchant.active_pgs.join(", ")}`}
      />
      <div className="p-4 sm:p-10 max-w-6xl space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 auto-rows-fr">
          <KpiCard
            icon={TrendingUp}
            label="Latest volume"
            value={fmtKpi(kpis.current_period_volume)}
            sub={fmtPeriod(kpis.current_period)}
            testid="merchant-kpi-volume"
          />
          <KpiCard
            icon={Building2}
            label="Active MIDs"
            value={kpis.active_mids}
            sub={`Across ${kpis.active_pgs} PG${kpis.active_pgs === 1 ? "" : "s"}`}
            testid="merchant-kpi-mids"
          />
          <KpiCard
            icon={Receipt}
            label="Active PGs"
            value={kpis.active_pgs}
            sub={merchant.active_pgs.join(", ") || "—"}
            testid="merchant-kpi-pgs"
          />
          <SettlementMixCard mix={kpis.settlement_mix} totalMids={kpis.active_mids} />
        </div>

        <Section title="Last 6 months — volume trend" testid="merchant-trend">
          <div className="p-5">
            {trend.length === 0 ? (
              <div className="text-[12.5px] text-[var(--muted)]">No volumes yet.</div>
            ) : (
              <div style={{ minHeight: 240, width: "100%" }}>
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={trend} margin={{ top: 5, right: 8, bottom: 0, left: 0 }}>
                    <defs>
                      <linearGradient id="merchVolFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--accent)" stopOpacity={0.32} />
                        <stop offset="100%" stopColor="var(--accent)" stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" />
                    <XAxis dataKey="period" tickFormatter={fmtPeriod} tick={{ fontSize: 11, fill: "var(--muted)" }} />
                    <YAxis tickFormatter={(v) => `₹${fmtINR(v)}`} tick={{ fontSize: 11, fill: "var(--muted)" }} width={70} />
                    <Tooltip
                      labelFormatter={fmtPeriod}
                      formatter={(v) => `₹ ${fmtINR(v)}`}
                      contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4 }}
                    />
                    <Area type="monotone" dataKey="volume" stroke="var(--accent)" strokeWidth={2} fill="url(#merchVolFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>

        <Section title="Latest period — by gateway" testid="merchant-gw-breakdown">
          <div className="p-5">
            {gateway_breakdown.length === 0 ? (
              <div className="text-[12.5px] text-[var(--muted)]">No data.</div>
            ) : (
              <div style={{ minHeight: 200, width: "100%" }}>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={gateway_breakdown} layout="vertical" margin={{ top: 5, right: 12, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={(v) => `₹${fmtINR(v)}`} tick={{ fontSize: 11, fill: "var(--muted)" }} />
                    <YAxis type="category" dataKey="gateway" tick={{ fontSize: 11, fill: "var(--muted)" }} width={120} />
                    <Tooltip
                      formatter={(v) => `₹ ${fmtINR(v)}`}
                      contentStyle={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 4 }}
                    />
                    <Bar dataKey="volume" fill="var(--accent)" radius={[0, 2, 2, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============== MERCHANT VOLUMES ==========================================

export function MerchantVolumes() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [period, setPeriod] = useState("");
  const [sort, setSort] = useState({ field: "period", dir: "desc", numeric: false });
  useEffect(() => {
    api.get("/portal/merchant/volumes")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load volumes"));
  }, []);

  const periods = useMemo(() => {
    if (!rows) return [];
    return Array.from(new Set(rows.map((r) => r.period))).sort().reverse();
  }, [rows]);

  const visible = useMemo(() => {
    if (!rows) return [];
    const ql = q.trim().toLowerCase();
    let out = rows;
    if (period) out = out.filter((r) => r.period === period);
    if (ql) {
      out = out.filter((r) =>
        String(r.merchant_id || "").toLowerCase().includes(ql)
        || String(r.gateway || "").toLowerCase().includes(ql)
        || String(r.lob || "").toLowerCase().includes(ql)
      );
    }
    return applySort(out, sort);
  }, [rows, q, period, sort]);

  // ---- KPIs --------------------------------------------------------------
  // Computed against the *currently filtered* view so toggling the period or
  // searching a single MID instantly re-summarises. Latest period is always
  // surfaced separately so the merchant sees their newest month at a glance
  // even when the table is filtered to something else.
  const kpis = useMemo(() => {
    if (!rows) return null;
    const latestPeriod = periods[0] || "";
    const latestRows = latestPeriod ? rows.filter((r) => r.period === latestPeriod) : [];
    const latestVol = latestRows.reduce((a, r) => a + (Number(r.processed_volume) || 0), 0);
    const filteredVol = visible.reduce((a, r) => a + (Number(r.processed_volume) || 0), 0);
    const filteredTxn = visible.reduce((a, r) => a + (Number(r.transaction_count) || 0), 0);
    // Average ticket size on the filtered view: volume / txns (₹ per txn)
    const avgTicket = filteredTxn > 0 ? filteredVol / filteredTxn : 0;
    const monthsCovered = new Set(visible.map((r) => r.period)).size;
    const midsInScope = new Set(visible.map((r) => r.merchant_id)).size;
    return { latestPeriod, latestVol, filteredVol, filteredTxn, avgTicket, monthsCovered, midsInScope };
  }, [rows, visible, periods]);

  if (rows === null) return <PortalLoading />;
  return (
    <div data-testid="merchant-volumes">
      <PageHeader title="Volumes" subtitle="Processed volume per MID, gateway, and month" />
      <div className="p-4 sm:p-10 max-w-6xl space-y-6">
        {kpis && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 auto-rows-fr" data-testid="merchant-vol-kpis">
            <KpiCard
              icon={TrendingUp}
              label="Latest period volume"
              value={fmtKpi(kpis.latestVol)}
              sub={kpis.latestPeriod ? fmtPeriod(kpis.latestPeriod) : "—"}
              testid="mv-kpi-latest"
            />
            <KpiCard
              icon={Banknote}
              label={period || q ? "Filtered volume" : "Total volume"}
              value={fmtKpi(kpis.filteredVol)}
              sub={`${kpis.monthsCovered} ${kpis.monthsCovered === 1 ? "month" : "months"} · ${kpis.midsInScope} MID${kpis.midsInScope === 1 ? "" : "s"}`}
              testid="mv-kpi-filtered"
            />
            <KpiCard
              icon={Receipt}
              label="Transactions"
              value={kpis.filteredTxn ? Number(kpis.filteredTxn).toLocaleString("en-IN") : "—"}
              sub={period || q ? "In current view" : "All-time"}
              testid="mv-kpi-txn"
            />
            <KpiCard
              icon={FileText}
              label="Avg ticket size"
              value={kpis.avgTicket ? `₹ ${fmtINR(kpis.avgTicket, 0)}` : "—"}
              sub="Volume ÷ Txn count"
              testid="mv-kpi-avg-ticket"
            />
          </div>
        )}
        <Section title={`${visible.length} of ${rows.length} rows`}>
          <FilterBar
            q={q} setQ={setQ}
            placeholder="Search MID / Gateway / LOB…"
            filters={[
              { key: "period", label: "Month", value: period, options: periods.map((p) => ({ value: p, label: fmtPeriod(p) })), onChange: setPeriod },
            ]}
            onClear={() => { setQ(""); setPeriod(""); }}
            testidPrefix="mv"
          />
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead>
                <tr>
                  <SortHeader field="period" sort={sort} setSort={setSort}>Period</SortHeader>
                  <SortHeader field="merchant_id" sort={sort} setSort={setSort}>MID</SortHeader>
                  <SortHeader field="gateway" sort={sort} setSort={setSort}>Gateway</SortHeader>
                  <SortHeader field="lob" sort={sort} setSort={setSort}>LOB</SortHeader>
                  <SortHeader field="settlement_type" sort={sort} setSort={setSort}>Settlement</SortHeader>
                  <SortHeader field="processed_volume" sort={sort} setSort={setSort} align="right" numeric>Volume</SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--muted)]">No volumes match the current filters.</td></tr>}
                {visible.map((r) => (
                  <tr key={`${r.period}-${r.merchant_id}`}>
                    <td className="num">{fmtPeriod(r.period)}</td>
                    <td className="num">{r.merchant_id}</td>
                    <td>{r.gateway || <span className="text-[var(--muted)]">—</span>}</td>
                    <td>{r.lob || <span className="text-[var(--muted)]">—</span>}</td>
                    <td>{r.settlement_type ? <SettlementBadge value={r.settlement_type} /> : <span className="text-[var(--muted)]">—</span>}</td>
                    <td className="text-right num">₹ {fmtINR(r.processed_volume)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============== MERCHANT COMMERCIALS ======================================

export function MerchantCommercials() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [gw, setGw] = useState("");
  const [lob, setLob] = useState("");
  const [settle, setSettle] = useState("");
  const [sort, setSort] = useState({ field: "merchant_id", dir: "asc", numeric: false });
  useEffect(() => {
    api.get("/portal/merchant/commercials")
      .then((r) => setRows(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load commercials"));
  }, []);

  const gateways = useMemo(() => Array.from(new Set((rows || []).map((r) => r.gateway).filter(Boolean))).sort(), [rows]);
  const lobs = useMemo(() => Array.from(new Set((rows || []).map((r) => r.lob).filter(Boolean))).sort(), [rows]);

  const visible = useMemo(() => {
    if (!rows) return [];
    const ql = q.trim().toLowerCase();
    let out = rows;
    if (gw) out = out.filter((r) => r.gateway === gw);
    if (lob) out = out.filter((r) => r.lob === lob);
    if (settle) out = out.filter((r) => (r.settlement_type || "") === settle);
    if (ql) {
      out = out.filter((r) =>
        String(r.merchant_id || "").toLowerCase().includes(ql)
        || String(r.gateway || "").toLowerCase().includes(ql)
        || String(r.lob || "").toLowerCase().includes(ql)
        || String(r.status || "").toLowerCase().includes(ql)
      );
    }
    return applySort(out, sort);
  }, [rows, q, gw, lob, settle, sort]);

  if (rows === null) return <PortalLoading />;
  return (
    <div data-testid="merchant-commercials">
      <PageHeader title="Commercials" subtitle="The rate you pay each gateway, per MID + LOB" />
      <div className="p-4 sm:p-10 max-w-4xl">
        <Section title={`${visible.length} of ${rows.length} agreements`}>
          <FilterBar
            q={q} setQ={setQ}
            placeholder="Search MID / Gateway / LOB / Status…"
            filters={[
              { key: "gw", label: "Gateway", value: gw, options: gateways, onChange: setGw },
              { key: "lob", label: "LOB", value: lob, options: lobs, onChange: setLob },
              { key: "settle", label: "Settlement", value: settle, options: ["IS", "T+1"], onChange: setSettle },
            ]}
            onClear={() => { setQ(""); setGw(""); setLob(""); setSettle(""); }}
            testidPrefix="mc"
          />
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead>
                <tr>
                  <SortHeader field="merchant_id" sort={sort} setSort={setSort}>MID</SortHeader>
                  <SortHeader field="gateway" sort={sort} setSort={setSort}>Gateway</SortHeader>
                  <SortHeader field="lob" sort={sort} setSort={setSort}>LOB</SortHeader>
                  <SortHeader field="settlement_type" sort={sort} setSort={setSort}>Settlement</SortHeader>
                  <SortHeader field="merchant_rate" sort={sort} setSort={setSort} align="right" numeric>Rate</SortHeader>
                  <SortHeader field="status" sort={sort} setSort={setSort}>Status</SortHeader>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-[var(--muted)]">No data matches the current filters.</td></tr>}
                {visible.map((r) => (
                  <tr key={r.merchant_id}>
                    <td className="num">{r.merchant_id}</td>
                    <td>{r.gateway || <span className="text-[var(--muted)]">—</span>}</td>
                    <td>{r.lob || <span className="text-[var(--muted)]">—</span>}</td>
                    <td>{r.settlement_type ? <SettlementBadge value={r.settlement_type} /> : <span className="text-[var(--muted)]">—</span>}</td>
                    <td className="text-right num">{(Number(r.merchant_rate || 0) * 100).toFixed(3)}%</td>
                    <td><Badge>{r.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      </div>
    </div>
  );
}

// ============== shared bits ===============================================

// Filter-bar primitives — single search box + N <select>'s + active-filter
// pill row + clear-all link. Keeps every portal table looking identical.
function FilterBar({ q, setQ, placeholder, filters = [], onClear, testidPrefix }) {
  const active = (filters || []).filter((f) => f.value);
  const hasAny = !!q || active.length > 0;
  return (
    <div className="px-3 py-2 border-b border-[var(--line)] flex flex-wrap items-center gap-2 bg-[var(--surface-2)]/40">
      <div className="relative flex-1 min-w-[180px] max-w-xs">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input
          data-testid={`${testidPrefix}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder || "Search…"}
          className="w-full h-8 pl-7 pr-3 text-[12.5px] bg-white border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ("")}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-[var(--muted)] hover:text-[var(--ink)]"
            data-testid={`${testidPrefix}-search-clear`}
            aria-label="Clear search"
          >
            <X size={12} />
          </button>
        )}
      </div>
      {filters.map((f) => (
        <select
          key={f.key}
          data-testid={`${testidPrefix}-filter-${f.key}`}
          value={f.value}
          onChange={(e) => f.onChange(e.target.value)}
          className="h-8 px-2 text-[12.5px] bg-white border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)]"
          aria-label={f.label}
        >
          <option value="">{f.label}: All</option>
          {f.options.map((o) => (
            <option key={typeof o === "string" ? o : o.value} value={typeof o === "string" ? o : o.value}>
              {typeof o === "string" ? o : o.label}
            </option>
          ))}
        </select>
      ))}
      {hasAny && (
        <button
          type="button"
          onClick={onClear}
          className="text-[12px] text-[var(--accent)] hover:underline ml-auto"
          data-testid={`${testidPrefix}-filter-clear`}
        >
          Clear filters
        </button>
      )}
    </div>
  );
}

// Sortable column header. `field` is the row key to sort by; clicking flips
// asc/desc/cleared (3-state toggle for portals — finance teams want sort
// state to be predictable). `numeric` flips the comparator to numeric.
function SortHeader({ children, field, sort, setSort, align, numeric }) {
  const active = sort.field === field;
  const dir = active ? sort.dir : null;
  const cycle = () => {
    if (!active) setSort({ field, dir: "asc", numeric });
    else if (dir === "asc") setSort({ field, dir: "desc", numeric });
    else setSort({ field: null, dir: null });
  };
  return (
    <th
      role="button"
      onClick={cycle}
      className={`cursor-pointer select-none hover:text-[var(--ink)] ${align === "right" ? "text-right" : ""}`}
      data-testid={`sort-${field}`}
      data-sort={active ? dir : "none"}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {active && (dir === "asc" ? <ArrowUp size={10} className="text-[var(--accent)]" /> : <ArrowDown size={10} className="text-[var(--accent)]" />)}
      </span>
    </th>
  );
}

// Tiny `sort.field` aware comparator. Strings are case-folded; numbers
// coerce; missing values sort to the bottom regardless of direction.
function applySort(rows, sort) {
  if (!sort?.field) return rows;
  const { field, dir, numeric } = sort;
  const sign = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a?.[field];
    const bv = b?.[field];
    const aMissing = av == null || av === "";
    const bMissing = bv == null || bv === "";
    if (aMissing && bMissing) return 0;
    if (aMissing) return 1;
    if (bMissing) return -1;
    if (numeric) return (Number(av) - Number(bv)) * sign;
    return String(av).toLowerCase().localeCompare(String(bv).toLowerCase()) * sign;
  });
}


function PortalLoading() {
  return (
    <div className="min-h-[60vh] grid place-items-center">
      <div className="inline-flex items-center gap-2 text-[13px] text-[var(--muted)]">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, testid }) {
  // `num` (tabular monospace) + nowrap is correct for short numerics like "3"
  // but breaks the layout for textual values like "Fixed base rate" — they
  // overflow the card into the next column. Detect numeric values (digits,
  // separators, em/en dash, %) and only apply the mono+nowrap treatment then;
  // textual values wrap with break-words and slightly tighter leading.
  const isNumeric = typeof value === "number"
    || (typeof value === "string" && /^[\d,.\s%—–-]+$/.test(value));
  return (
    <div
      data-testid={testid}
      className="rise bg-[var(--surface)] border border-[var(--line)] p-6 flex flex-col h-full"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
        <Icon size={12} strokeWidth={2.2} className="text-[var(--accent)]" />
        {label}
      </div>
      <div className="mt-3 flex items-baseline gap-2 min-w-0">
        <div
          className={`text-2xl xl:text-[28px] font-semibold text-[var(--ink)] min-w-0 ${
            isNumeric ? "num whitespace-nowrap" : "leading-tight break-words"
          }`}
        >
          {value}
        </div>
      </div>
      <div className="text-[11px] text-[var(--muted)] mt-auto pt-1.5">{sub}</div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold mb-1.5">{label}</div>
      <div className={`num text-[18px] font-semibold ${highlight ? "text-[var(--accent)]" : "text-[var(--ink)]"}`}>{value}</div>
    </div>
  );
}

function Badge({ children }) {
  return (
    <span className="inline-flex items-center text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
      {children}
    </span>
  );
}

// Settlement Type chip — IS = Instant (accent), T+1 = next-day (muted).
// Mirrors the styling used on the admin Merchants table so the merchant
// recognises the same vocabulary across both surfaces.
function SettlementBadge({ value }) {
  const isInstant = value === "IS";
  const cls = isInstant
    ? "bg-[var(--accent)]/10 text-[var(--accent)] border-[var(--accent)]/30"
    : "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span
      title={isInstant ? "Instant Settlement — funds same-day" : "T+1 — next business day"}
      className={`inline-flex items-center text-[10.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}
      data-testid={`settlement-${value}`}
    >
      {value}
    </span>
  );
}

// Settlement-mix KPI card — splits the merchant's MIDs into IS / T+1 /
// Unset so they see at a glance which settlement plan they're on. If every
// MID is IS, the card shows a single bold chip; mixed merchants get a
// breakdown.
function SettlementMixCard({ mix, totalMids }) {
  const m = mix || { IS: 0, "T+1": 0, Unset: 0 };
  const present = [
    ["IS", m.IS],
    ["T+1", m["T+1"]],
  ].filter(([, n]) => n > 0);
  return (
    <div
      data-testid="merchant-kpi-settlement"
      className="rise bg-[var(--surface)] border border-[var(--line)] p-6 flex flex-col h-full"
    >
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
        <Receipt size={12} strokeWidth={2.2} className="text-[var(--accent)]" />
        Settlement Type
      </div>
      <div className="mt-3 flex items-baseline flex-wrap gap-2">
        {present.length === 0 ? (
          <div className="text-[12px] text-[var(--muted)]">Not yet assigned</div>
        ) : present.length === 1 ? (
          <>
            <SettlementBadge value={present[0][0]} />
            <div className="text-[12px] text-[var(--muted)]">all {totalMids} MIDs</div>
          </>
        ) : (
          present.map(([k, n]) => (
            <span key={k} className="inline-flex items-center gap-1">
              <SettlementBadge value={k} />
              <span className="num text-sm font-semibold text-[var(--ink)]">{n}</span>
            </span>
          ))
        )}
      </div>
      {m.Unset > 0 && (
        <div className="text-[10.5px] text-amber-700 mt-1.5">{m.Unset} MID{m.Unset === 1 ? "" : "s"} unset — ask your account manager.</div>
      )}
      <div className="text-[11px] text-[var(--muted)] mt-auto pt-1.5">
        IS = same-day · T+1 = next business day
      </div>
    </div>
  );
}


// ============== PARTNER · RATE CARDS ======================================
//
// Read-only panel — partner sees their own per-(gateway, lob) base rates.
// Revenue-share partners (no rate cards) see their share % + a friendly note
// instead of an empty table. NEVER expose admin-side margins or other
// partners' data here.

export function PartnerRateCards() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/portal/partner/rate-cards")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load rate cards"));
  }, []);

  if (!data) return <PortalLoading />;

  const isRevShare = (data.compensation_model || "").toLowerCase() === "revenue_share";
  const cards = data.rate_cards || [];
  const visible = q
    ? cards.filter((c) =>
        (c.gateway + " " + c.lob).toLowerCase().includes(q.toLowerCase()))
    : cards;

  return (
    <div data-testid="partner-rate-cards">
      <PageHeader title="Rate Cards" subtitle="Your agreed base rates per gateway and line of business" />
      <div className="p-4 sm:p-10 max-w-4xl space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 auto-rows-fr">
          <KpiCard
            icon={Layers}
            label="Compensation model"
            value={isRevShare ? "Revenue share" : "Fixed base rate"}
            sub={isRevShare ? `${(Number(data.revenue_share_pct || 0) * 100).toFixed(1)}% of net commission` : `${cards.length} rate card${cards.length === 1 ? "" : "s"}`}
            testid="prc-kpi-model"
          />
          <KpiCard
            icon={Building2}
            label="Gateways"
            value={new Set(cards.map((c) => c.gateway)).size || "—"}
            sub="Where you have base rates"
            testid="prc-kpi-gateways"
          />
          <KpiCard
            icon={Percent}
            label="LOBs"
            value={new Set(cards.map((c) => c.lob)).size || "—"}
            sub="Lines of business covered"
            testid="prc-kpi-lobs"
          />
        </div>

        {isRevShare && cards.length === 0 && (
          <div className="p-5 border border-[var(--line)] bg-[var(--surface)] rounded-sm">
            <div className="text-[13px] text-[var(--ink)] font-medium mb-1">You're on a revenue-share model.</div>
            <p className="text-[12.5px] text-[var(--muted)] leading-relaxed">
              You earn <b>{(Number(data.revenue_share_pct || 0) * 100).toFixed(1)}%</b> of the net commission generated by your tagged merchants — no per-LOB base rate applies. When onboarding a new merchant, you only need to provide the <b>Merchant Rate (MDR)</b>; we'll handle the rest internally.
            </p>
          </div>
        )}

        <Section title={cards.length === 0 ? "No rate cards" : `${cards.length} rate card${cards.length === 1 ? "" : "s"}`}>
          <div className="px-4 pt-3 pb-2 flex items-center gap-2">
            <div className="relative w-full sm:w-72">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search gateway / LOB…"
                className="w-full pl-8 pr-3 h-9 border border-[var(--line-2)] bg-[var(--surface)] rounded-sm text-[13px] outline-none focus:border-[var(--accent)]"
                data-testid="prc-search"
              />
            </div>
          </div>
          <div className="overflow-auto">
            <table className="table-data w-full">
              <thead>
                <tr>
                  <th>Gateway</th>
                  <th>LOB</th>
                  <th className="text-right">Your base rate</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-[var(--muted)]">
                    {cards.length === 0 ? "No rate cards on file. Contact your admin for setup." : "No matches for current search."}
                  </td></tr>
                )}
                {visible.map((c, i) => (
                  <tr key={`${c.gateway}-${c.lob}-${i}`}>
                    <td className="font-medium">{c.gateway}</td>
                    <td>{c.lob}</td>
                    <td className="text-right num">{(Number(c.partner_base_rate) * 100).toFixed(2)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>

        <div className="text-[11.5px] text-[var(--muted)] leading-relaxed border-l-2 border-[var(--line-2)] pl-3">
          When onboarding a new merchant, the <b>Merchant Rate</b> you set must be greater than or equal to your base rate for that (gateway, LOB) combination. The portal will validate this for you automatically.
        </div>
      </div>
    </div>
  );
}

// ============== MERCHANT · API DOCS ======================================
//
// One-stop shortcut to public PG developer docs. We keep the curated list
// minimal — only the PGs we currently support live on the platform. Each
// card opens the official docs in a new tab.

const PG_DOCS = [
  {
    name: "Cashfree",
    blurb: "Payments, payouts, settlements, webhooks — full API + SDKs.",
    url: "https://www.cashfree.com/docs/api-reference/overview",
    accent: "from-emerald-50 to-emerald-100",
  },
  {
    name: "Razorpay",
    blurb: "Standard checkout, recurring, marketplace, route — REST API reference.",
    url: "https://razorpay.com/docs/api/",
    accent: "from-blue-50 to-blue-100",
  },
  {
    name: "Lyra",
    blurb: "Hosted payment page, REST API & webhook events.",
    url: "https://docs.lyra.com/en/rest/V4.0/api/",
    accent: "from-amber-50 to-amber-100",
  },
];

export function MerchantApiDocs() {
  return <ApiDocsView role="merchant" />;
}

export function PartnerApiDocs() {
  return <ApiDocsView role="partner" />;
}

// Shared implementation used by both Merchant and Partner portals — the
// docs themselves don't differ by role, but the page subtitle does.
function ApiDocsView({ role }) {
  const subtitle = role === "partner"
    ? "Developer references for the payment gateways your merchants use"
    : "Developer references for the payment gateways you're integrated with";
  return (
    <div data-testid={`${role}-api-docs`}>
      <PageHeader title="API Documentation" subtitle={subtitle} />
      <div className="p-4 sm:p-10 max-w-5xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PG_DOCS.map((pg) => (
            <a
              key={pg.name}
              href={pg.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`pg-doc-${pg.name.toLowerCase()}`}
              className={`group rise bg-gradient-to-br ${pg.accent} border border-[var(--line)] p-5 flex flex-col gap-3 hover:border-[var(--ink-2)] transition-all rounded-sm`}
            >
              <div className="flex items-center justify-between">
                <div className="h-9 w-9 rounded-sm bg-white grid place-items-center border border-[var(--line)]">
                  <BookOpen size={16} className="text-[var(--ink)]" strokeWidth={2} />
                </div>
                <ExternalLink size={14} className="text-[var(--muted)] group-hover:text-[var(--ink)]" />
              </div>
              <div className="font-display text-[20px] tracking-tight text-[var(--ink)]">{pg.name}</div>
              <p className="text-[12.5px] text-[var(--ink-2)] leading-relaxed">{pg.blurb}</p>
              <div className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mt-auto inline-flex items-center gap-1.5">
                Open docs <ExternalLink size={11} />
              </div>
            </a>
          ))}
        </div>
        <div className="p-4 border border-[var(--line)] bg-[var(--surface-2)] rounded-sm text-[12.5px] text-[var(--muted)] leading-relaxed">
          Need integration help, API keys, or webhook URLs for your specific MID? Reach out via the <b>Support</b> page in your portal — your account manager will respond within one business day.
        </div>
      </div>
    </div>
  );
}

// ============== SUPPORT (shared by Partner + Merchant) ===================
//
// Lightweight support landing. The "Keep yourself updated" CTA sends users
// to the BizAssist WhatsApp channel for product updates. FAQ list differs
// per portal — partners care about commission cycles, merchants care about
// their volumes / settlement / MID lifecycle.

const WHATSAPP_CHANNEL_URL = "https://whatsapp.com/channel/0029Vb7y2qGFCCoXQawYnD0B";

const MERCHANT_FAQS = [
  {
    q: "When are my settlements credited?",
    a: "Settlement timing depends on the PG and your contracted plan (T+0 / T+1 / IS-Instant). Check the Settlement Holidays page for non-business days that delay credits, and your PG's dashboard for the actual UTR for each batch.",
  },
  {
    q: "Why does my volume here differ from the PG dashboard?",
    a: "We reconcile against settled volumes only — refunds, chargebacks, and pending captures are excluded. PG dashboards usually show gross authorisations, which include those.",
  },
  {
    q: "How do I request a new MID?",
    a: "Open the MID Requests page from the sidebar and click Request new MID. Pick the gateway and LOB; your account manager picks it up the same business day.",
  },
  {
    q: "How do I update my POC / contact / bank details?",
    a: "Email partnership@bizassist.online with the change — for compliance reasons, bank-detail changes need a fresh cancelled cheque + authorisation letter.",
  },
];

const PARTNER_FAQS = [
  {
    q: "When are commissions credited?",
    a: "Commissions for a closed period are credited to your registered bank account within 7 working days of the period close, after PG-side reconciliation. You'll get an email + statement entry the moment a payout is marked paid.",
  },
  {
    q: "How is my commission calculated?",
    a: "If you're on a fixed-base-rate plan, you earn (Merchant Rate − Your Base Rate) × Volume per (gateway, LOB). If you're on revenue share, you earn a fixed % of the net commission generated by your tagged merchants. Open the Rate Cards page for your specific rates.",
  },
  {
    q: "How do I onboard a new merchant or request a new MID for an existing one?",
    a: "Use the Onboarding page from the sidebar. New merchants need name + POC + gateway + LOB + merchant rate (≥ your base rate). Existing merchants only need gateway + LOB + new merchant rate.",
  },
  {
    q: "Where can I see my upcoming and past payouts?",
    a: "The Payouts page shows your next-due amount + period-by-period history. Statements has the formal invoice/CN documents for filing.",
  },
];

function SupportPanel({ portalKey, faqs, contactEmail = "partnership@bizassist.online" }) {
  return (
    <div data-testid={`${portalKey}-support`}>
      <PageHeader
        title="Support"
        subtitle="We're here to help — reach out, follow updates, find answers"
      />
      <div className="p-4 sm:p-10 max-w-4xl space-y-6">
        {/* Hero CTA — Keep yourself updated (WhatsApp channel) */}
        <div className="rise bg-gradient-to-br from-[#dcf8c6] to-[#bff09c] border border-emerald-300 p-6 sm:p-8 rounded-sm">
          <div className="flex items-start gap-4">
            <div className="h-12 w-12 rounded-sm bg-[#25D366] grid place-items-center shrink-0 shadow-sm">
              <MessageCircle size={22} className="text-white" strokeWidth={2.2} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-900 font-semibold mb-1">Stay in the loop</div>
              <div className="font-display text-[22px] sm:text-[26px] tracking-tight text-emerald-950 leading-tight">Keep yourself updated</div>
              <p className="text-[13px] text-emerald-900/80 mt-2 leading-relaxed">
                Follow our WhatsApp channel for product updates, feature announcements, settlement-cycle changes, and PG outage alerts — straight to your phone.
              </p>
              <a
                href={WHATSAPP_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="whatsapp-updates-btn"
                className="mt-4 inline-flex items-center gap-2 h-10 px-5 bg-[#25D366] hover:bg-[#1fb558] text-white font-medium rounded-sm text-[13.5px] transition-colors shadow-sm"
              >
                <MessageCircle size={14} strokeWidth={2.2} />
                Join the channel
                <ExternalLink size={12} className="opacity-80" />
              </a>
            </div>
          </div>
        </div>

        {/* Contact options grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-[var(--line)] bg-[var(--surface)] p-5 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-sm bg-[var(--accent)]/10 grid place-items-center">
                <LifeBuoy size={14} className="text-[var(--accent)]" />
              </div>
              <div className="text-[14px] font-medium text-[var(--ink)]">Email support</div>
            </div>
            <p className="text-[12.5px] text-[var(--muted)] leading-relaxed mb-3">
              For account, settlement, or commercials queries — drop us a line and we'll respond within one business day.
            </p>
            <a
              href={`mailto:${contactEmail}`}
              data-testid="support-email-link"
              className="text-[12.5px] text-[var(--accent)] font-medium hover:underline underline-offset-2"
            >
              {contactEmail}
            </a>
          </div>
          <div className="border border-[var(--line)] bg-[var(--surface)] p-5 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="h-8 w-8 rounded-sm bg-[var(--accent)]/10 grid place-items-center">
                <BookOpen size={14} className="text-[var(--accent)]" />
              </div>
              <div className="text-[14px] font-medium text-[var(--ink)]">PG developer docs</div>
            </div>
            <p className="text-[12.5px] text-[var(--muted)] leading-relaxed mb-3">
              Direct links to Cashfree, Razorpay, and Lyra reference docs for integration work.
            </p>
            <a
              href={`/portal/${portalKey}/api-docs`}
              data-testid="support-api-docs-link"
              className="text-[12.5px] text-[var(--accent)] font-medium hover:underline underline-offset-2"
            >
              Browse API docs →
            </a>
          </div>
        </div>

        {/* Inline FAQ — kept short so it doesn't compete with the hero CTA */}
        <div className="border border-[var(--line)] bg-[var(--surface)] rounded-sm">
          <div className="px-5 py-3 border-b border-[var(--line)] text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">
            Frequently asked
          </div>
          <div className="divide-y divide-[var(--line)]">
            {faqs.map((f) => (
              <FaqRow key={f.q} q={f.q}>{f.a}</FaqRow>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function MerchantSupport() {
  return <SupportPanel portalKey="merchant" faqs={MERCHANT_FAQS} />;
}

export function PartnerSupport() {
  return <SupportPanel portalKey="partner" faqs={PARTNER_FAQS} />;
}

function FaqRow({ q, children }) {
  return (
    <details className="group">
      <summary className="px-5 py-3 cursor-pointer flex items-center justify-between hover:bg-[var(--surface-2)]">
        <span className="text-[13px] text-[var(--ink)] font-medium">{q}</span>
        <span className="text-[var(--muted)] text-[11px] group-open:rotate-180 transition-transform">▾</span>
      </summary>
      <div className="px-5 pb-4 text-[12.5px] text-[var(--ink-2)] leading-relaxed">{children}</div>
    </details>
  );
}
